import { MeteoMetricRow } from './components/MeteoMetricRow.component';
import { MeteoWeatherChart } from './components/MeteoWeatherChart.component';
import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import { ForecastParamCode } from 'pleinchamp-api-client';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { MoreMetricsButton } from '@components/buttons/MoreMetricsButton/MoreMetricsButton.component';
import { MeteoWeatherTableHeader } from '@meteo/components/MeteoWeatherTable/MeteoWeatherTableHeader/MeteoWeatherTableHeader.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { Spacing } from '@styles/Spacing.style';
import { isServer } from '@utils/server';
import {
  Direction,
  filterParamsToDisplay,
  getScroll,
  WEATHER_TABLE_CELL_WIDTH,
  WeatherTableContentProps,
} from '../utils';
import styles from './MeteoWeatherTableContent.module.scss';

export function MeteoWeatherTableContent({
  dates,
  weatherCodes,
  isChartEnabled,
  format15Days,
  metrics,
  displayHHLink,
  isPro,
  timeframe,
  thirtySixHours,
  withDays,
  onLoadMore,
  isMoreToDisplay,
  isLoading,
  top,
  coordinates,
  ...rest
}: WeatherTableContentProps) {
  const isSmartphone = useIsBreakpointDown(Breakpoint.m);
  const overFlowingContainerRef = useRef<HTMLDivElement | null>(null);
  const { city } = useContext(MeteoHomeContext);

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>();

  const hasChart = isChartEnabled && !isSmartphone && !isLoading;

  function handleNavigation(direction: Direction) {
    if (!overFlowingContainerRef.current) {
      return;
    }

    const scrollStep = WEATHER_TABLE_CELL_WIDTH * 4;
    const offset = getScroll(overFlowingContainerRef, direction);

    if (!offset) {
      return;
    }

    if (direction === Direction.Left) {
      overFlowingContainerRef.current.scrollTo({
        behavior: 'smooth',
        left:
          Math.round(overFlowingContainerRef.current.scrollLeft / WEATHER_TABLE_CELL_WIDTH) * WEATHER_TABLE_CELL_WIDTH -
          scrollStep,
      });
    }
    if (direction === Direction.Right) {
      overFlowingContainerRef.current.scrollTo({
        behavior: 'smooth',
        left:
          Math.round(overFlowingContainerRef.current.scrollLeft / WEATHER_TABLE_CELL_WIDTH) * WEATHER_TABLE_CELL_WIDTH +
          scrollStep +
          Spacing.m,
      });
    }
  }

  const onScroll = useCallback(() => {
    // The sticky component doesn't allow to get a ref on the inner wrapper thus we need to query it
    const tableHeaderScrollingElement = overFlowingContainerRef?.current?.querySelector('.sticky-inner-wrapper');
    if (tableHeaderScrollingElement) {
      tableHeaderScrollingElement.scroll({
        behavior: 'auto',
        left: overFlowingContainerRef?.current?.scrollLeft,
      });
    }
  }, []);

  if (isServer()) {
    // SSR & sticky header, not a good match…
    return null;
  }

  return (
    <div
      className={`${styles.forecastTable} ${thirtySixHours ? styles.thirtySixHours : ''} ${withDays ? styles.withDays : ''}`}
      style={{ paddingBottom: `${city.data ? 0 : 310}px`, width: format15Days && !isPro && !isSmartphone ? '51%' : '' }}
    >
      <div ref={overFlowingContainerRef} className="overflowing-container" onScroll={onScroll}>
        <MeteoWeatherTableHeader
          coordinates={coordinates}
          dates={dates}
          format15Days={format15Days}
          formatHourByHour={!format15Days && !thirtySixHours}
          handleNavigation={handleNavigation}
          hoveredIndex={hoveredIndex}
          isLoading={isLoading}
          overFlowingContainerRef={overFlowingContainerRef}
          setHoveredIndex={setHoveredIndex}
          top={top}
          weatherCodes={weatherCodes}
          withDays={withDays}
        />
        {/* <BecomeProOverlay15d asOverlay i18nTitle="actu.become-pro.15d-title" visible={format15Days && !isLoading} /> */}
        <table {...rest} onMouseLeave={() => setHoveredIndex(undefined)}>
          <tbody>
            {hasChart && (
              <MeteoWeatherChart
                hoveredIndex={hoveredIndex}
                is36h={thirtySixHours}
                metrics={metrics}
                onMouseEnter={setHoveredIndex}
              />
            )}
            {!hasChart && !format15Days && (
              <MeteoMetricRow
                hoveredIndex={hoveredIndex}
                is15Days={format15Days}
                is36h={thirtySixHours}
                isLoading={isLoading}
                metric={metrics[ForecastParamCode.airTemperature]}
                metricCode={ForecastParamCode.airTemperature}
                onMouseEnter={setHoveredIndex}
              />
            )}
            {Object.keys(metrics)
              .filter((e) => filterParamsToDisplay(e as ForecastParamCode))
              .map((key) => (
                <MeteoMetricRow
                  key={key}
                  hoveredIndex={hoveredIndex}
                  is15Days={format15Days}
                  is36h={thirtySixHours}
                  isLoading={isLoading}
                  metric={metrics[key as ForecastParamCode]}
                  metricCode={key as ForecastParamCode}
                  onMouseEnter={setHoveredIndex}
                />
              ))}
          </tbody>
        </table>
      </div>
      <MoreMetricsButton disabled={!isMoreToDisplay} onClick={onLoadMore} />
    </div>
  );
}
